import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { retryOnException } from '@atlaskit/frontend-utilities/retry-operation';
import { WORKED_EVENT } from '../../../common/types/query';
import { packageName, packageVersion } from '../../../common/utils/analytics/package-context';
import { fetchActivitiesWithExperience } from '../../../common/utils/analytics/utils';
import { postResource } from '../../../utils/fetch';
import { GraphQLFragmentError } from '../../../utils/fetch/errors/GraphQLFragmentError';
import { GraphQLJSError } from '../../../utils/fetch/errors/GraphQLJSError';
import { GraphQLNoResultsError } from '../../../utils/fetch/errors/GraphQLNoResults';
import { hydratePolymorphicData } from '../../../utils/hydrator';
import { hydrateConfluenceIcon, hydratedValueToString, hydrateIcon } from '../../../utils/hydrator/utils';
import HydratorConfigClient from '../../hydrator-config-client';
import { ActivityClient } from '../base';
import graphQLErrorHandler from './error-handling';
import { generateGraphQLRequest } from './query';
import { activityRoot, filterInvalidFragments, filtersToMetadata, isEmptyActivitiesWithErrorsResponse, parseRootContainerARI, rollupActivitiesData } from './utils';
const CONFLUENCE_COMMENT_TYPENAMES = ['ConfluenceComment', 'ConfluenceFooterComment', 'ConfluenceInlineComment'];
const IGNORED_HTTP_ERRORS = new Map([
// ignore all 401 errors
[401, [/.*/]], [403, [/Invalid CORS request/]]]);
const IGNORED_ERROR_MESSAGES = ['The operation was aborted.'];
const DEFAULT_TAGS = {
  source: packageName,
  sourcePackageVersion: packageVersion
};
export class ActivityClientV3 extends ActivityClient {
  constructor(_product, baseUrl, logger, configFetch) {
    super('v3', _product, {
      'X-ExperimentalApi': 'ActivityV3Api'
    }, baseUrl);
    _defineProperty(this, "setProduct", product => {
      this.product = product;
    });
    _defineProperty(this, "captureException", (error, tags) => {
      if (this.clientHandlers) {
        this.clientHandlers.captureException(error, {
          ...DEFAULT_TAGS,
          ...tags
        });
      }
    });
    _defineProperty(this, "captureWarning", (message, tags) => {
      if (this.clientHandlers) {
        this.clientHandlers.captureWarning(message, {
          ...DEFAULT_TAGS,
          ...tags
        });
      }
    });
    _defineProperty(this, "fetchHydratorConfig", async () => await this.hydratorConfigClient.fetchConfig());
    _defineProperty(this, "handleGraphQLErrors", errors => {
      const invalidFragments = [];
      if (Array.isArray(errors)) {
        for (const err of errors) {
          this.captureException(new GraphQLJSError(err));
          const invalidFragment = graphQLErrorHandler(err);
          if (invalidFragment) {
            invalidFragments.push(invalidFragment);
          }
        }
        return invalidFragments;
      }
      return [];
    });
    _defineProperty(this, "fetchActivitiesV3", async (experience, retryMS, filters, activityQueries, deduplicateActivities = true) => {
      var _response$data, _activityRoot, _activityRoot$workedO, _response$data$activi, _response$data$activi2, _response$data$activi3, _activityRoot2, _activityRoot2$all;
      let {
        fragments,
        hydrators
      } = await this.fetchHydratorConfig();
      experience.mark('GeneratedQueryWithFilters_start');
      let response;
      try {
        response = await retryOnException(async () => {
          const request = generateGraphQLRequest(this.product, filters, activityQueries, fragments);
          experience.mark('GeneratedQueryWithFilters_end');
          experience.mark('FetchedActivities_start');
          experience.addMetadata({
            filters: filtersToMetadata(filters)
          });
          const res = await postResource(this.baseUrl, request, retryMS, {
            ...this.headers
          });
          experience.mark('FetchedActivities_end');
          const invalidFragments = this.handleGraphQLErrors(res === null || res === void 0 ? void 0 : res.errors);
          if (invalidFragments.length > 0) {
            fragments = filterInvalidFragments(invalidFragments, fragments);
            throw new GraphQLFragmentError(`Missing fragments: [${invalidFragments.toString()}]`);
          }
          return res;
        }, {
          intervalsMS: [0, 0, 0],
          retryOn: err => [GraphQLFragmentError].some(retriableErr => err instanceof retriableErr),
          captureException: this.captureException
        });
      } catch (error) {
        var _IGNORED_HTTP_ERRORS$;
        if (!error || !((_IGNORED_HTTP_ERRORS$ = IGNORED_HTTP_ERRORS.get(error.statusCode)) !== null && _IGNORED_HTTP_ERRORS$ !== void 0 && _IGNORED_HTTP_ERRORS$.some(regExp => regExp.test(error.message))) && !IGNORED_ERROR_MESSAGES.includes(error.message)) {
          throw error;
        }
      }
      if (isEmptyActivitiesWithErrorsResponse(response)) {
        throw new GraphQLNoResultsError(response.errors);
      }
      if (!response || !((_response$data = response.data) !== null && _response$data !== void 0 && _response$data.activity)) {
        return {
          workedOn: [],
          viewed: [],
          all: []
        };
      }
      experience.mark('TransformedActivityItems_start');
      const workedOn = transformActivityItem('workedOn', ((_activityRoot = activityRoot(response.data.activity)) === null || _activityRoot === void 0 ? void 0 : (_activityRoot$workedO = _activityRoot.workedOn) === null || _activityRoot$workedO === void 0 ? void 0 : _activityRoot$workedO.edges) || [], hydrators, this.captureException);

      // We don't need the activity root function here as viewed only exists on myActivity
      const viewed = transformActivityItem('viewed', ((_response$data$activi = response.data.activity) === null || _response$data$activi === void 0 ? void 0 : (_response$data$activi2 = _response$data$activi.myActivity) === null || _response$data$activi2 === void 0 ? void 0 : (_response$data$activi3 = _response$data$activi2.viewed) === null || _response$data$activi3 === void 0 ? void 0 : _response$data$activi3.edges) || [], hydrators, this.captureException);
      const all = transformActivityItem('all', ((_activityRoot2 = activityRoot(response.data.activity)) === null || _activityRoot2 === void 0 ? void 0 : (_activityRoot2$all = _activityRoot2.all) === null || _activityRoot2$all === void 0 ? void 0 : _activityRoot2$all.edges) || [], hydrators, this.captureException);
      experience.mark('TransformedActivityItems_end');
      const activities = {
        viewed,
        workedOn,
        all
      };
      return deduplicateActivities ? rollupActivitiesData(activities) : activities;
    });
    _defineProperty(this, "fetchActivities", async (retryMS, filters, activityQueries, deduplicateActivities = true) => {
      return fetchActivitiesWithExperience(experience => this.fetchActivitiesV3(experience, retryMS, filters, activityQueries, deduplicateActivities), 'v3');
    });
    this.clientHandlers = logger;
    this.hydratorConfigClient = new HydratorConfigClient({
      captureException: this.captureException,
      captureWarning: this.captureWarning
    }, _product, configFetch);
  }
}
function transformActivityItem(query, activities, hydrators, captureException) {
  return activities.reduce((acc, item) => {
    if (item && item.node && item.node.object && item.node.event) {
      const {
        object,
        event
      } = item.node;
      if (!object.product || !object.type) {
        return acc;
      }
      const containers = [];
      let c = object.contributors || [];
      const contributors = c.reduce((contributors, contributor) => {
        if (contributor.profile) {
          const {
            accountId,
            picture
          } = contributor.profile;
          contributors.push({
            accountId,
            name: contributor.profile.name,
            picture
          });
        }
        return contributors;
      }, []);
      const hydratedData = hydratePolymorphicData(hydrators, object.data, captureException);
      if (hydratedData) {
        var _object$data, _object$data$containe;
        if (hydratedData.key) {
          containers.push({
            name: hydratedValueToString(hydratedData.key, ' ')
          });
        }
        if (hydratedData.containers) {
          containers.push({
            name: hydratedValueToString(hydratedData.containers, ' ')
          });
        }
        const actor = event.actor ? {
          name: event.actor.name,
          picture: event.actor.picture,
          accountId: event.actor.accountId
        } : undefined;

        // The V3 API returns "Created" for a new comment, we want this to display as "Commented"
        const eventType = object.type === 'comment' && event.eventType === WORKED_EVENT.CREATED ? WORKED_EVENT.COMMENTED : event.eventType;
        acc.push({
          actor,
          query,
          containers,
          contributors,
          eventType,
          object: {
            rootContainerIdentifier: parseRootContainerARI(object.rootContainerId),
            icon: CONFLUENCE_COMMENT_TYPENAMES.includes(object.data.__typename) ? hydrateConfluenceIcon((_object$data = object.data) === null || _object$data === void 0 ? void 0 : (_object$data$containe = _object$data.container) === null || _object$data$containe === void 0 ? void 0 : _object$data$containe.__typename, hydratedData.icon, hydratedData.staticFallbacks) : hydrateIcon(hydratedData.icon, hydratedData.staticFallbacks),
            id: object.id,
            deduplicateId: hydratedValueToString(hydratedData.deduplicateId) || object.id,
            localResourceId: hydratedValueToString(hydratedData.localResourceId),
            name: hydratedValueToString(hydratedData.name, ' '),
            product: object.product,
            subProduct: object.subProduct,
            type: object.type,
            url: hydratedValueToString(hydratedData.url)
          },
          timestamp: event.timestamp
        });
      }
    }
    return acc;
  }, []);
}